import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import { Token } from '../types/auth';

export class BaseApiClient {
  public axios: AxiosInstance;
  private baseURL: string;
  protected tokenSetCallbacks: Set<(token: string) => void | Promise<void>> = new Set();
  protected tokenClearCallbacks: Set<() => void | Promise<void>> = new Set();

  protected pendingRequests: Map<string, Promise<any>> = new Map();
  protected refreshPromise: Promise<Token> | null = null;

  constructor() {
    // Use environment variable for API URL, fallback to proxy in development
    this.baseURL = import.meta.env.VITE_API_URL || '';
    this.axios = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': i18next.language || 'en',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      withCredentials: true,
      timeout: 10000 // 10 second timeout
    });

    // API Client configured

    this.setupInterceptors();
    this.setupLanguageHandler();
  }

  private setupLanguageHandler() {
    // Subscribe to language changes
    i18next.on('languageChanged', (lng) => {
      this.axios.defaults.headers.common['Accept-Language'] = lng;
    });
  }

  private setupInterceptors() {
    // Add auth token and transform business details in requests
    this.axios.interceptors.request.use((config) => {
      // Add auth token
      const token = this.getAuthToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add CSRF token for protected endpoints
      if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
        const csrfToken = this.getCsrfToken();
        if (csrfToken && config.headers) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }

      // Transform business details from camelCase to snake_case
      if (config.url === '/api/v1/auth/register' && config.data?.business_details) {
        const businessDetails = config.data.business_details;
        config.data = {
          ...config.data,
          business_details: {
            ...businessDetails,
            vat_number: businessDetails.vatNumber,
          }
        };
        // Remove the camelCase property
        delete config.data.business_details.vatNumber;
      }

      return config;
    });

    // Handle responses
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // API error occurred

        // Don't attempt refresh for these endpoints
        const skipRefreshEndpoints = [
          '/api/v1/auth/refresh',
          '/api/v1/auth/login',
          '/api/v1/auth/register'
        ];

        const originalRequest = error.config;
        if (error.response?.status === 401 && 
            !originalRequest._retry && 
            !skipRefreshEndpoints.includes(originalRequest.url)) {
          originalRequest._retry = true;

          // Received 401, attempting token refresh

          // Try to refresh token if available
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              // Use existing refresh promise or create new one
              if (!this.refreshPromise) {
                // Starting token refresh
                this.refreshPromise = this.refreshToken(refreshToken)
                  .then(response => {
                    // Token refresh successful
                    return response;
                  })
                  .catch(error => {
                    // Token refresh failed
                    throw error;
                  })
                  .finally(() => {
                    this.refreshPromise = null;
                  });
              } else {
                // Using existing refresh promise
              }

              const response = await this.refreshPromise;
              
              // Store both tokens
              await this.setAuthToken(response.access_token);
              if (response.refresh_token) {
                localStorage.setItem('refresh_token', response.refresh_token);
              }

              // Update the authorization header for both default and request
              this.axios.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;

              // Add small delay to ensure token is set
              await new Promise(resolve => setTimeout(resolve, 100));

              // Retry the original request with updated headers
              return this.axios(originalRequest);
            } catch (refreshError: any) {
              // Token refresh failed
              
              // Check if it's a network error
              if (!refreshError.response) {
                // Network error during refresh, retrying original request
                return this.axios(originalRequest);
              }

              // Clear tokens and redirect to login
              await this.clearAuthToken();
              window.location.href = '/auth/login';
              return Promise.reject(refreshError);
            }
          } else {
            // No refresh token available, clear tokens and redirect to login
            await this.clearAuthToken();
            window.location.href = '/auth/login';
            return Promise.reject(error);
          }
        }

        // For other errors, just reject
        return Promise.reject(error);
      }
    );
  }

  protected getRequestKey(method: string, url: string, data?: any): string {
    return `${method}:${url}:${JSON.stringify(data)}`;
  }

  protected async makeRequest<T>(
    method: string,
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const requestKey = this.getRequestKey(method, url, data);
    
    // Check if there's already a pending request
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }

    // Create the request promise
    const requestPromise = (async () => {
      try {
        const response = await (method === 'get' 
          ? this.axios.get<T>(url, config)
          : method === 'post'
          ? this.axios.post<T>(url, data, config)
          : method === 'put'
          ? this.axios.put<T>(url, data, config)
          : method === 'patch'
          ? this.axios.patch<T>(url, data, config)
          : this.axios.delete<T>(url, config));
        return response.data;
      } finally {
        // Clean up the pending request
        this.pendingRequests.delete(requestKey);
      }
    })();

    // Store the promise
    this.pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  // Generic request methods
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.makeRequest<T>('get', url, undefined, config);
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.makeRequest<T>('post', url, data, config);
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.makeRequest<T>('put', url, data, config);
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.makeRequest<T>('delete', url, undefined, config);
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.makeRequest<T>('patch', url, data, config);
  }

  private async refreshToken(refreshToken: string): Promise<Token> {
    const response = await this.axios.post<Token>('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  }

  // Token management methods
  async setAuthToken(token: string): Promise<void> {
    // Setting auth token
    localStorage.setItem('token', token);
    // Update axios default headers
    this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Notify all registered callbacks
    const notifyPromises = Array.from(this.tokenSetCallbacks).map(async (callback) => {
      try {
        await callback(token);
      } catch (error) {
        // Error in token set callback
      }
    });

    // Wait for all callbacks to complete
    await Promise.all(notifyPromises);

    // Add small delay to ensure token is fully set
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  async clearAuthToken(): Promise<void> {
    // Clearing auth token
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // Remove authorization header
    delete this.axios.defaults.headers.common['Authorization'];
    
    // Notify all registered callbacks
    const notifyPromises = Array.from(this.tokenClearCallbacks).map(async (callback) => {
      try {
        await callback();
      } catch (error) {
        // Error in token clear callback
      }
    });

    // Wait for all callbacks to complete
    await Promise.all(notifyPromises);

    // Add small delay to ensure token is fully cleared
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  getCsrfToken(): string | null {
    // Get CSRF token from cookie
    const match = document.cookie.match(new RegExp('(^| )csrf_token=([^;]+)'));
    return match ? match[2] : null;
  }

  // Add callback registration methods
  onTokenSet(callback: (token: string) => void | Promise<void>): () => void {
    this.tokenSetCallbacks.add(callback);
    return () => this.tokenSetCallbacks.delete(callback);
  }

  onTokenClear(callback: () => void | Promise<void>): () => void {
    this.tokenClearCallbacks.add(callback);
    return () => this.tokenClearCallbacks.delete(callback);
  }
}
